import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import {
  RELATIONSHIPS,
  type Relationship,
  type ParentDraft,
  type ParentLink,
  blankParentDraft,
} from '../lib/studentTypes.js'
import { Card, CardBody, CardHeader } from './Card.js'
import { Section } from './Section.js'
import { Button } from './Button.js'
import { Field, Input, Select, Checkbox } from './Field.js'
import { PhoneField } from './PhoneField.js'
import { friendlyError } from '../lib/errors.js'

export type { ParentDraft, ParentLink }

// --------------------------------------------------------------------------
// Create mode
// --------------------------------------------------------------------------
interface CreateProps {
  mode: 'create'
  parents: ParentDraft[]
  onChange: (next: ParentDraft[]) => void
}

// --------------------------------------------------------------------------
// Manage mode
// --------------------------------------------------------------------------
interface ManageProps {
  mode: 'manage'
  studentId: string
  parents: ParentLink[]
}

type StudentParentSectionProps = CreateProps | ManageProps

// --------------------------------------------------------------------------

interface StudentDetailCache {
  student: unknown
  parents: ParentLink[]
  classes: { id: string; name: string }[]
}

export function StudentParentSection(props: StudentParentSectionProps) {
  if (props.mode === 'create') {
    return <CreateParentSection parents={props.parents} onChange={props.onChange} />
  }
  return <ManageParentSection studentId={props.studentId} parents={props.parents} />
}

// --------------------------------------------------------------------------
// Create mode component
// --------------------------------------------------------------------------
function CreateParentSection({
  parents,
  onChange,
}: {
  parents: ParentDraft[]
  onChange: (next: ParentDraft[]) => void
}) {
  const update = (i: number, patch: Partial<ParentDraft>) => {
    onChange(parents.map((p, idx) => (idx === i ? { ...p, ...patch } : p)))
  }

  const remove = (i: number) => {
    onChange(parents.filter((_, idx) => idx !== i))
  }

  const add = () => {
    onChange([...parents, { ...blankParentDraft(), isPrimary: false }])
  }

  return (
    <Section
      title="Parent contacts"
      description="At least one parent is required. They'll be invited to the parent portal."
      actions={
        <Button type="button" variant="secondary" size="sm" onClick={add}>
          Add another parent
        </Button>
      }
    >
      <div className="space-y-3">
        {parents.map((p, i) => (
          <Card key={i}>
            <CardHeader
              title={`Parent ${i + 1}`}
              actions={
                parents.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-xs text-fg-muted hover:text-danger"
                  >
                    Remove
                  </button>
                ) : null
              }
            />
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <Input
                    value={p.fullName}
                    onChange={(e) => update(i, { fullName: e.target.value })}
                    required
                  />
                </Field>
                <Field label="Relationship">
                  <Select
                    value={p.relationship}
                    onChange={(e) => update(i, { relationship: e.target.value as Relationship })}
                  >
                    {RELATIONSHIPS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Email" required>
                  <Input
                    type="email"
                    value={p.email}
                    onChange={(e) => update(i, { email: e.target.value })}
                    required
                  />
                </Field>
                <PhoneField
                  label="Phone"
                  required
                  hint="WhatsApp reminders"
                  value={p.phone}
                  onChange={(phone) => update(i, { phone })}
                />
              </div>
              <Checkbox
                label="Primary contact"
                checked={p.isPrimary}
                onChange={(v) => update(i, { isPrimary: v })}
              />
            </CardBody>
          </Card>
        ))}
      </div>
    </Section>
  )
}

// --------------------------------------------------------------------------
// Manage mode component
// --------------------------------------------------------------------------
function ManageParentSection({
  studentId,
  parents,
}: {
  studentId: string
  parents: ParentLink[]
}) {
  const qc = useQueryClient()

  // Per-link editing state: linkId -> draft fields
  const [editing, setEditing] = useState<
    Record<string, { fullName: string; phone: string; relationship: string; isPrimary: boolean }>
  >({})
  const [saving, setSaving] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)

  // Add-parent sub-form
  const [addDraft, setAddDraft] = useState<ParentDraft>(blankParentDraft())
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)

  const startEdit = (p: ParentLink) => {
    setEditing((curr) => ({
      ...curr,
      [p.id]: {
        fullName: p.parentName ?? '',
        phone: p.parentPhone ?? '',
        relationship: p.relationship ?? 'guardian',
        isPrimary: p.isPrimary,
      },
    }))
  }

  const cancelEdit = (linkId: string) => {
    setEditing((curr) => {
      const next = { ...curr }
      delete next[linkId]
      return next
    })
  }

  const saveEdit = async (linkId: string) => {
    const draft = editing[linkId]
    if (!draft) return
    setSaving(linkId)
    try {
      const res = await api<ParentLink>(`/api/v1/students/${studentId}/parents/${linkId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          fullName: draft.fullName || undefined,
          phone: draft.phone || undefined,
          relationship: draft.relationship || undefined,
          isPrimary: draft.isPrimary,
        }),
      })
      if ('error' in res) throw new Error(res.error.message)
      const updated = res.data
      qc.setQueryData<StudentDetailCache>(['students', studentId], (cache) => {
        if (!cache) return cache
        return {
          ...cache,
          parents: cache.parents.map((p) => (p.id === linkId ? updated : p)),
        }
      })
      cancelEdit(linkId)
    } catch {
      // error stays visible via the disabled state — saving is reset below
    } finally {
      setSaving(null)
    }
  }

  const removeParent = async (linkId: string, name: string) => {
    if (!confirm(`Remove ${name || 'this parent'}? This cannot be undone.`)) return
    setRemoving(linkId)
    try {
      const res = await api<{ id: string }>(`/api/v1/students/${studentId}/parents/${linkId}`, {
        method: 'DELETE',
      })
      if ('error' in res) throw new Error(res.error.message)
      qc.setQueryData<StudentDetailCache>(['students', studentId], (cache) => {
        if (!cache) return cache
        return {
          ...cache,
          parents: cache.parents.filter((p) => p.id !== linkId),
        }
      })
    } catch {
      // ignore - parent stays in the list
    } finally {
      setRemoving(null)
    }
  }

  const addParent = async () => {
    setAddError(null)
    setAdding(true)
    try {
      const res = await api<ParentLink>(`/api/v1/students/${studentId}/parents`, {
        method: 'POST',
        body: JSON.stringify({
          fullName: addDraft.fullName,
          email: addDraft.email,
          phone: addDraft.phone || undefined,
          relationship: addDraft.relationship,
          isPrimary: addDraft.isPrimary,
        }),
      })
      if ('error' in res) {
        setAddError(friendlyError(res.error))
        return
      }
      const newLink = res.data
      qc.setQueryData<StudentDetailCache>(['students', studentId], (cache) => {
        if (!cache) return cache
        return {
          ...cache,
          parents: [...cache.parents, newLink],
        }
      })
      setAddDraft(blankParentDraft())
      setShowAdd(false)
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add parent')
    } finally {
      setAdding(false)
    }
  }

  return (
    <Section
      title="Parent contacts"
      description={`${parents.length} linked contact${parents.length === 1 ? '' : 's'}`}
      actions={
        !showAdd ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowAdd(true)}>
            Add parent
          </Button>
        ) : null
      }
    >
      <div className="space-y-3">
        {parents.map((p) => {
          const draft = editing[p.id]
          const isSaving = saving === p.id
          const isRemoving = removing === p.id

          return (
            <Card key={p.id}>
              <CardHeader
                title={p.parentName ?? p.parentEmail}
                description={
                  <span className="text-xs text-fg-subtle">
                    {p.parentEmail}
                    {p.isPrimary ? ' · primary' : ''}
                  </span>
                }
                actions={
                  draft ? (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        loading={isSaving}
                        onClick={() => void saveEdit(p.id)}
                      >
                        Save
                      </Button>
                      <button
                        type="button"
                        onClick={() => cancelEdit(p.id)}
                        className="text-xs text-fg-muted hover:text-fg"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => startEdit(p)}
                        className="text-xs text-fg-muted hover:text-fg"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={isRemoving}
                        onClick={() => void removeParent(p.id, p.parentName ?? p.parentEmail)}
                        className="text-xs text-fg-muted hover:text-danger disabled:opacity-50"
                      >
                        {isRemoving ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  )
                }
              />
              {draft ? (
                <CardBody className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Full name">
                      <Input
                        value={draft.fullName}
                        onChange={(e) =>
                          setEditing((curr) => ({
                            ...curr,
                            [p.id]: { ...curr[p.id]!, fullName: e.target.value },
                          }))
                        }
                      />
                    </Field>
                    <Field label="Relationship">
                      <Select
                        value={draft.relationship}
                        onChange={(e) =>
                          setEditing((curr) => ({
                            ...curr,
                            [p.id]: { ...curr[p.id]!, relationship: e.target.value },
                          }))
                        }
                      >
                        {RELATIONSHIPS.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <PhoneField
                      label="Phone"
                      optional
                      value={draft.phone}
                      onChange={(phone) =>
                        setEditing((curr) => ({
                          ...curr,
                          [p.id]: { ...curr[p.id]!, phone },
                        }))
                      }
                    />
                    <Field label="Primary contact">
                      <div className="flex items-center h-9">
                        <Checkbox
                          label="Mark as primary"
                          checked={draft.isPrimary}
                          onChange={(v) =>
                            setEditing((curr) => ({
                              ...curr,
                              [p.id]: { ...curr[p.id]!, isPrimary: v },
                            }))
                          }
                        />
                      </div>
                    </Field>
                  </div>
                </CardBody>
              ) : null}
            </Card>
          )
        })}

        {showAdd ? (
          <Card>
            <CardHeader
              title="Add a parent"
              actions={
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false)
                    setAddDraft(blankParentDraft())
                    setAddError(null)
                  }}
                  className="text-xs text-fg-muted hover:text-fg"
                >
                  Cancel
                </button>
              }
            />
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <Input
                    value={addDraft.fullName}
                    onChange={(e) => setAddDraft((d) => ({ ...d, fullName: e.target.value }))}
                    required
                  />
                </Field>
                <Field label="Relationship">
                  <Select
                    value={addDraft.relationship}
                    onChange={(e) =>
                      setAddDraft((d) => ({ ...d, relationship: e.target.value as Relationship }))
                    }
                  >
                    {RELATIONSHIPS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Email" required>
                  <Input
                    type="email"
                    value={addDraft.email}
                    onChange={(e) => setAddDraft((d) => ({ ...d, email: e.target.value }))}
                    required
                  />
                </Field>
                <PhoneField
                  label="Phone"
                  optional
                  hint="WhatsApp reminders"
                  value={addDraft.phone}
                  onChange={(phone) => setAddDraft((d) => ({ ...d, phone }))}
                />
              </div>
              <Checkbox
                label="Primary contact"
                checked={addDraft.isPrimary}
                onChange={(v) => setAddDraft((d) => ({ ...d, isPrimary: v }))}
              />
              {addError ? <p className="text-sm text-danger">{addError}</p> : null}
              <Button
                type="button"
                loading={adding}
                disabled={!addDraft.fullName.trim() || !addDraft.email.trim()}
                onClick={() => void addParent()}
              >
                Add parent
              </Button>
            </CardBody>
          </Card>
        ) : null}
      </div>
    </Section>
  )
}
